import {
  Box,
  Button,
  Grid,
  Heading,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import ModalBillingEntity from "@components/modules/billing-entity/ModalBillingEntity/ModalBillingEntity";
import {
  ButtonLink,
  Card,
  CardHeader,
  ExpandableItem,
  LabeledItem,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type BillingEntityModalFragment,
  useDeleteBillingEntityMutation,
} from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, useContext } from "react";
import { BillingEntityModel } from "./models";
import { PageClientDetailCtx } from "./PageClientDetail";

const SectionAddress: FunctionComponent<
  React.PropsWithChildren<{
    address: BillingEntityModel;
  }>
> = observer(function SectionAddress({ address: i }) {
  const store = useContext(PageClientDetailCtx);
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();
  const splitMails = i.invoice_email?.replaceAll(" ", "").split(",");

  const onEditBillingEntity = (payload: BillingEntityModalFragment) => {
    editModal.onClose();

    runInAction(() => {
      i.applies_vat = payload.applies_vat;
      i.legal_name = payload.legal_name;
      i.internal_name = payload.internal_name;
      i.street = payload.street;
      i.city = payload.city;
      i.zip = payload.zip;
      i.registration_no = payload.registration_no ?? undefined;
      i.tax_no = payload.tax_no ?? undefined;
      i.vat_no = payload.vat_no ?? undefined;
      i.invoice_email = payload.invoice_email ?? undefined;
      i.payment_due = payload.payment_due ?? undefined;
      i.note = payload.note ?? undefined;
      i.country = payload.country ?? undefined;
      i.defaultCurrency = payload.defaultCurrency ?? undefined;
      i.language = payload.language;
      i.workspaceCompany = payload.workspaceCompany;
      i.workspaceBankAccount = payload.workspaceBankAccount;
      i.disable_email_notification = payload.disable_email_notification;
    });
  };

  const [del] = useDeleteBillingEntityMutation({
    onCompleted(data) {
      store.deleteBillingAddressById(data.deleteBillingEntity.id);
    },
    ...onError(),
  });

  return (
    <Card.Subsection>
      <ExpandableItem
        title={i.internal_name}
        popoverActions={[
          { content: t`Edit`, onAction: editModal.onOpen, iconName: "edit-02" },
          {
            content: t`Delete`,
            onAction: deleteModal.onOpen,
            iconName: "trash-03",
          },
        ]}
      >
        <Grid
          gap="4"
          templateColumns={{
            base: "1fr",
            md: "60% 40%",
          }}
        >
          <Box>
            <LabeledItem label={t`Title on invoice`} verticalAlign="start">
              {i.legal_name}
            </LabeledItem>
            <LabeledItem label={t`Street`}>{i.street}</LabeledItem>
            <LabeledItem label={t`City`}>{i.city}</LabeledItem>
            <LabeledItem label={t`ZIP`}>{i.zip}</LabeledItem>
            <LabeledItem label={t`Country`}>{i.country.name}</LabeledItem>
            <LabeledItem label={t`Invoice e-mail addresses`}>
              {splitMails?.map((email, index) => (
                <ButtonLink
                  key={email}
                  href={`mailto:${email}`}
                  isExternal
                  variant="link"
                >
                  {email}
                  {index < splitMails?.length - 1 ? <Text>,&nbsp;</Text> : null}
                </ButtonLink>
              ))}
            </LabeledItem>
            <LabeledItem label={t`Preferred legal entity`}>
              {i.workspaceCompany.name}
            </LabeledItem>
            <LabeledItem label={t`Preferred IBAN`}>
              {i.workspaceBankAccount?.iban}
            </LabeledItem>
            <LabeledItem label={t`Note`}>{i.note}</LabeledItem>
          </Box>
          <Box>
            <LabeledItem label={t`Reg. no`}>{i.registration_no}</LabeledItem>
            <LabeledItem label={t`Tax ID`}>{i.tax_no}</LabeledItem>
            <LabeledItem label={t`VAT no`}>{i.vat_no}</LabeledItem>
            <LabeledItem label={t`Applies VAT`}>
              {i.applies_vat ? t`Yes` : t`No`}
            </LabeledItem>
            <LabeledItem label={t`Payment due`}>
              {`${i.payment_due} ${i.payment_due === 1 ? t`day` : t`days`}`}
            </LabeledItem>
            <LabeledItem label={t`Default currency`}>
              {i.defaultCurrency.code}
            </LabeledItem>
            <LabeledItem label={t`Language on invoice`}>
              {i.language}
            </LabeledItem>
            <LabeledItem label={t`Disable email notifications`}>
              {i.disable_email_notification ? t`Yes` : t`No`}
            </LabeledItem>
          </Box>
        </Grid>
      </ExpandableItem>
      {editModal.isOpen && (
        <ModalBillingEntity
          {...editModal}
          billingEntity={i}
          client={store.client}
          onSubmit={onEditBillingEntity}
        />
      )}
      {deleteModal.isOpen && (
        <ModalConfirm
          {...deleteModal}
          onConfirm={() => del({ variables: { id: i.id } })}
        />
      )}
    </Card.Subsection>
  );
});

export const CardBillingAddresses: FunctionComponent<
  React.PropsWithChildren<object>
> = observer(function CardBillingAddresses() {
  const store = useContext(PageClientDetailCtx);
  const client = store.client;

  const addModal = useDisclosure();
  const onAddBillingEntity = (payload: BillingEntityModalFragment) => {
    addModal.onClose();
    billingEntities.push(new BillingEntityModel(payload));
  };

  if (!client) return null;

  const { billingEntities } = client;

  return (
    <Box
      w="full"
      borderTop="1px"
      borderTopColor="gray.200"
      borderBottom="1px"
      borderBottomColor="gray.200"
    >
      <CardHeader
        icon="wallet-02"
        title={
          <Heading size="md">
            <Trans>Billing addresses</Trans>
          </Heading>
        }
        action={
          <Button
            colorScheme="grey"
            leftIcon={<Icon name="plus" />}
            {...addModal.getButtonProps()}
            variant="outline"
          >
            <Trans>Add new billing address</Trans>
          </Button>
        }
      />

      <Card.Section>
        {billingEntities.map((i) => (
          <SectionAddress address={i} key={i.id} />
        ))}
      </Card.Section>

      <ModalBillingEntity
        {...addModal.getDisclosureProps()}
        {...addModal}
        client={store.client}
        onSubmit={onAddBillingEntity}
      />
    </Box>
  );
});
